import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
   Typography,
  Fade
} from '@mui/material';
import { SendOutlined } from '@mui/icons-material';
import config from '../utils/config';

interface MessageInputProps {
  onSendMessage: (text: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}

const MessageInput: React.FC<MessageInputProps> = ({ 
  onSendMessage, 
  disabled = false,
  placeholder = "Type your message..."
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const maxLength = config.maxMessageLength;

  // Keep input focused to maintain keyboard open (Google Messages behavior)
  useEffect(() => {
    if (inputRef.current && !disabled) {
      // Initial focus after component mounts
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [disabled]);
  const isOverLimit = charCount > maxLength;
  const canSend = message.trim().length > 0 && !isOverLimit && !disabled && !isSending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canSend) return;

    const messageText = message.trim();
    setIsSending(true);

    try {
      await onSendMessage(messageText);
      setMessage('');
      setCharCount(0);
      
      // Focus back on input after sending
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error('Send message error:', error);
      // Keep the message in the input so user can retry
    } finally {
      setIsSending(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessage(value);
    setCharCount(value.length);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  // Auto-focus on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 500);
    return () => clearTimeout(timer);
  }, []);


  return (
    <Box sx={{ px: 1.5, py: 0.5 }}>
        {/* Character count indicator */}
        {charCount > maxLength * 0.8 && (
          <Fade in>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                textAlign: 'right',
                mb: 1,
                color: isOverLimit ? 'error.main' : 'warning.main'
              }}
            >
              {charCount}/{maxLength}
            </Typography>
          </Fade>
        )}

        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0.75 // Reduced from 1
          }}
        >
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            error={isOverLimit}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: '20px', // Apply borderRadius to TextField wrapper itself
              overflow: 'hidden', // Clip the background to rounded corners
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px', // Match container rounded corners
                backgroundColor: (theme) => 
                  theme.palette.mode === 'light' ? '#f0f0f0' : '#2a2a2a',
                border: (theme) => 
                  theme.palette.mode === 'light' ? '1px solid #d0d0d0' : '1px solid #444444',
                minHeight: '32px', // Reduced for compactness
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                },
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light' ? '#e8e8e8' : '#333333',
                  borderColor: (theme) =>
                    theme.palette.mode === 'light' ? '#c0c0c0' : '#555555'
                },
                '&.Mui-focused': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light' ? '#ffffff' : '#1a1a1a',
                  borderColor: '#007AFF', // iOS blue
                  boxShadow: '0 0 0 2px rgba(0, 122, 255, 0.2)'
                }
              },
              '& .MuiInputBase-input': {
                py: 0.5, // Further reduced for compactness
                px: 1.25, // Slightly reduced
                fontSize: 'clamp(0.875rem, 2.5vw, 1.2rem) !important', // Force exact match with !important
                lineHeight: '1.5 !important', // Match theme.typography.body1 exactly
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                color: (theme) => theme.palette.text.primary,
                '&::placeholder': {
                  color: (theme) =>
                    theme.palette.mode === 'light' ? '#8e8e93' : '#6e6e73',
                  opacity: 1
                }
              },
              // Force background color override to ensure it applies
              backgroundColor: (theme) => 
                theme.palette.mode === 'light' ? '#f0f0f0' : '#2a2a2a'
            }}
          />

          <IconButton
            type="submit"
            disabled={!canSend}
            sx={{
              backgroundColor: canSend ? 'primary.main' : (theme) =>
                theme.palette.mode === 'light' ? '#c7c7cc' : '#3a3a3a',
              color: '#ffffff',
              width: 32,  // Reduced from 36
              height: 32, // Reduced from 36
              minWidth: 32, // Reduced from 36
              // Center the send arrow horizontally and vertically
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Adjust position to align with TextField center
              marginBottom: '10px', // Fine-tune vertical alignment with TextField
              '&:hover': {
                backgroundColor: canSend ? 'primary.dark' : (theme) =>
                  theme.palette.mode === 'light' ? '#c7c7cc' : '#3a3a3a'
              },
              '&.Mui-disabled': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? '#c7c7cc' : '#3a3a3a',
                color: '#ffffff',
                opacity: 0.6
              },
              transition: 'all 0.2s ease-in-out',
              // Native iOS shadow
              boxShadow: canSend ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
              ...(isSending && {
                animation: 'pulse 1s infinite'
              })
            }}
          >
            <SendOutlined />
          </IconButton>
        </Box>

        {/* Helper text removed - placeholder text is sufficient */}

      {/* Pulse animation for sending state */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </Box>
  );
};

export default MessageInput;
